import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import moment from "moment";
import { MasterPage } from "../Shared/master";
import LiveTripTracking from "../map/LiveTripTracking";
import useLookup from "../../hooks/useLookup";
import useAbortSignal from "../../hooks/useAbortSignal";
import { isRequestCancelled } from "../../apis/useAxios";
import { showErrorToast, showInfoToast } from "../../helpers/message";
import { getAirportDropdown } from "../nonflyActivityLocation/activityLocationService";
import { getTracking, getVendors } from "./trackingService";
import { filterRows, hasLocation, sortRows, statusTiles } from "./trackingRules";
import TrackingFilterBar from "./TrackingFilterBar";
import TrackingSummary from "./TrackingSummary";
import VehicleList from "./VehicleList";
import TrackingMap from "./TrackingMap";
import VehicleDetailPanel from "./VehicleDetailPanel";
import "../reservations/dispatch/dispatch.css";
import "./trackingMapTheme.css";
import "./TrackingComponent.css";

const REFRESH_MS = 30000;
const NO_FILTERS = { airportCode: "", transporterId: 0 };

/**
 * Tracking: the vehicles on job (POST Tracking) on a map with the list beside it. Status tiles filter both; a click on a
 * card or a car opens the vehicle panel beside the map, from where Live Tracking opens (the shared LiveTripTracking).
 * Refreshes every 30 s while the tab is visible. Other screens may open it with location.state
 * { airportCode, transporterId, sourceUrl, sourceTitle } (e.g. Airport, Local Vendor).
 */
export function TrackingComponent() {
  const history = useHistory();
  const { state: from } = useLocation();
  const getSignal = useAbortSignal();
  const airline = sessionStorage.getItem("session_airlineCode") ?? "";
  const { items: airports } = useLookup(`airports:${airline}`, getAirportDropdown);
  const { items: vendors } = useLookup("transport-vendors", getVendors);

  const [filters, setFilters] = useState(() => ({ airportCode: from?.airportCode ?? "", transporterId: from?.transporterId ?? 0 }));
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [updatedAt, setUpdatedAt] = useState("");
  const [tile, setTile] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [liveOpen, setLiveOpen] = useState(false);
  const [fitKey, setFitKey] = useState(0);
  const [centerKey, setCenterKey] = useState(0);
  const applied = useRef(filters); // the filters of the last Search, used by the auto refresh
  const busy = useRef(false);
  const selectedRef = useRef(null);
  selectedRef.current = selectedId;

  const load = useCallback(
    async (query, fit) => {
      if (busy.current && !fit) return;
      busy.current = true;
      setLoading(true);
      try {
        const data = sortRows(await getTracking(query, { signal: getSignal() }));
        setRows(data);
        setUpdatedAt(moment().format("hh:mm:ss A"));
        if (fit) setFitKey((k) => k + 1);
        const picked = selectedRef.current;
        if (picked !== null && !data.some((v) => v.rideId === picked)) {
          showInfoToast("The selected vehicle is no longer on job.");
          setSelectedId(null);
          setLiveOpen(false);
        }
      } catch (err) {
        if (!isRequestCancelled(err)) showErrorToast(err?.message);
      } finally {
        busy.current = false;
        setLoading(false);
      }
    },
    [getSignal],
  );

  // First load with the filters given by the screen that opened Tracking.
  useEffect(() => {
    load(applied.current, true);
  }, [load]);

  // Auto refresh: every 30 s while the tab is visible, and at once when the user comes back to the tab.
  useEffect(() => {
    const tick = () => {
      if (!document.hidden) load(applied.current, false);
    };
    const timer = setInterval(tick, REFRESH_MS);
    document.addEventListener("visibilitychange", tick);
    return () => {
      clearInterval(timer);
      document.removeEventListener("visibilitychange", tick);
    };
  }, [load]);

  const search = () => {
    applied.current = filters;
    load(filters, true);
  };
  const clear = () => {
    setFilters(NO_FILTERS);
    setTile("");
    setSelectedId(null);
    setLiveOpen(false);
    applied.current = NO_FILTERS;
    load(NO_FILTERS, true);
  };
  const goBack = () => {
    if (from?.sourceTitle) sessionStorage.setItem("session_PageName", from.sourceTitle);
    history.push(from.sourceUrl);
  };

  const tiles = useMemo(() => statusTiles(rows), [rows]);
  const shown = useMemo(() => filterRows(rows, tile), [rows, tile]);
  const selected = rows.find((v) => v.rideId === selectedId) ?? null;

  // A tile whose status left after a refresh shows everything again.
  useEffect(() => {
    if (tile && !tiles.some((t) => t.id === tile)) setTile("");
  }, [tile, tiles]);

  const select = useCallback((rideId) => {
    setSelectedId(rideId);
    setLiveOpen(false);
  }, []);
  const closePanel = useCallback(() => {
    setSelectedId(null);
    setLiveOpen(false);
  }, []);

  return (
    <MasterPage title="Tracking">
      <div className="dsp-page trk-page">
        <TrackingFilterBar
          filters={filters}
          airports={airports}
          vendors={vendors}
          searching={loading}
          sourceTitle={from?.sourceTitle || "previous page"}
          onChange={(part) => setFilters((prev) => ({ ...prev, ...part }))}
          onSearch={search}
          onClear={clear}
          onBack={from?.sourceUrl ? goBack : null}
        />
        <TrackingSummary
          total={rows.length}
          tiles={tiles}
          tile={tile}
          onTile={setTile}
          updatedAt={updatedAt}
          refreshing={loading}
          onRefresh={() => load(applied.current, false)}
        />
        <p className="trk-hint">
          <i className="fas fa-info-circle" aria-hidden="true" /> Only vehicles on a job are shown.
        </p>

        <div className="tc-layout">
          <VehicleList
            rows={shown}
            total={rows.length}
            noGps={rows.filter((v) => !hasLocation(v)).length}
            selectedId={selectedId}
            loading={loading}
            onSelect={select}
          />
          <div className={`tc-map-wrap${selected ? " has-panel" : ""}`}>
            <TrackingMap rows={shown} selected={selected} onSelect={select} fitKey={fitKey} centerKey={centerKey} />
            {selected && (
              <VehicleDetailPanel
                vehicle={selected}
                onClose={closePanel}
                onLiveTracking={() => setLiveOpen(true)}
                onCenter={() => setCenterKey((k) => k + 1)}
              />
            )}
            {selected && liveOpen && selected.transactionId && (
              <div className="tc-live-panel">
                <div className="tc-live-panel-header">
                  <span>Live Trip Tracking · {selected.vehicleNo}</span>
                  <button type="button" className="tc-info-close" onClick={() => setLiveOpen(false)} aria-label="Close">
                    <i className="fas fa-times" />
                  </button>
                </div>
                <div className="tc-live-panel-body">
                  <LiveTripTracking
                    key={selected.transactionId}
                    airlineCode={airline}
                    transactionId={selected.transactionId}
                    employeeNumber={selected.employeeNo}
                    height="100%"
                    className="tc-live-panel-map"
                    onDeparted={() => setLiveOpen(false)}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </MasterPage>
  );
}

export default TrackingComponent;
